import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  Loader2, Globe, Palette, Package, ShoppingBag, Calendar, CalendarRange, Euro, Users, AlertCircle,
  RefreshCw, ExternalLink, Trash2, Search, Activity, Clock, Wrench, Zap, Building2, Heart, Scale, Home, Utensils, Dumbbell, Wheat,
} from "lucide-react";
import { toast } from "sonner";

const SECTOR_ICONS = { boulangerie: Wheat, plomberie: Wrench, avocat: Scale, gite: Home, restaurant: Utensils, coach: Dumbbell };
const FRESHNESS_META = {
  fresh: { label: "En ligne", color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
  stale: { label: "Idle", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", border: "border-amber-200" },
  offline: { label: "Hors ligne", color: "bg-red-500", text: "text-red-700", bg: "bg-red-50", border: "border-red-200" },
  never: { label: "Jamais", color: "bg-slate-400", text: "text-slate-700", bg: "bg-slate-50", border: "border-slate-200" },
  unknown: { label: "?", color: "bg-slate-300", text: "text-slate-600", bg: "bg-slate-50", border: "border-slate-200" },
};

function money(cents, currency = "EUR") {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency, maximumFractionDigits: 0 }).format((cents || 0) / 100);
}
function fmtRelative(min) {
  if (min == null) return "jamais";
  if (min < 1) return "à l'instant";
  if (min < 60) return `il y a ${min} min`;
  if (min < 1440) return `il y a ${Math.floor(min / 60)} h`;
  return `il y a ${Math.floor(min / 1440)} j`;
}

export default function AgencySitesDashboard() {
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [freshFilter, setFreshFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/agency/sites");
      setSites(r.data.sites || []);
    } catch (e) {
      toast.error("Impossible de charger les sites clients");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return sites.filter(s => {
      if (search) {
        const q = search.toLowerCase();
        if (!`${s.company_name || ""} ${s.site_url || ""} ${s.sector || ""}`.toLowerCase().includes(q)) return false;
      }
      if (sectorFilter && s.sector_preset_id !== sectorFilter) return false;
      if (freshFilter && s.freshness !== freshFilter) return false;
      return true;
    });
  }, [sites, search, sectorFilter, freshFilter]);

  const kpis = useMemo(() => {
    const total = sites.length;
    const online = sites.filter(s => s.freshness === "fresh").length;
    const offline = sites.filter(s => s.freshness === "offline" || s.freshness === "never").length;
    const revenue = sites.reduce((a, s) => a + (s.kpis?.revenue_30d_cents || 0), 0);
    const orders = sites.reduce((a, s) => a + (s.kpis?.orders_30d || 0), 0);
    const bookings = sites.reduce((a, s) => a + (s.kpis?.bookings_30d || 0), 0);
    const leads = sites.reduce((a, s) => a + (s.kpis?.leads_30d || 0), 0);
    return { total, online, offline, revenue, orders, bookings, leads };
  }, [sites]);

  const availableSectors = Array.from(new Set(sites.map(s => s.sector_preset_id).filter(Boolean)));

  const deleteSite = async (id) => {
    if (!window.confirm("Supprimer ce site de l'Accès Agence ? Cela n'affecte pas le site distant lui-même.")) return;
    try {
      await api.delete(`/agency/sites/${id}`);
      toast.success("Site retiré de l'Accès Agence");
      load();
    } catch { toast.error("Erreur"); }
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="agency-sites-dashboard">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Mes sites clients</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Monitoring temps réel de tous les COCKPIT déployés — secteur, thème, version, revenus.</p>
        </div>
        <Button variant="outline" onClick={load} data-testid="refresh-sites">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}/>Actualiser
        </Button>
      </header>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4" data-testid="kpi-sites-total">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Sites</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] mt-1">{kpis.total}</div>
          <div className="text-xs mt-1">
            <span className="text-emerald-600 font-semibold">{kpis.online} en ligne</span>
            {kpis.offline > 0 && <> · <span className="text-red-600">{kpis.offline} hors ligne</span></>}
          </div>
        </Card>
        <Card className="p-4" data-testid="kpi-sites-revenue">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Revenus 30j (agrégé)</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] mt-1">{money(kpis.revenue)}</div>
          <div className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">tous sites cumulés</div>
        </Card>
        <Card className="p-4" data-testid="kpi-sites-orders">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Commandes 30j</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] mt-1">{kpis.orders}</div>
          <div className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">{kpis.bookings} réservations</div>
        </Card>
        <Card className="p-4" data-testid="kpi-sites-leads">
          <div className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Leads 30j</div>
          <div className="font-display text-3xl font-black text-[color:var(--brand-navy)] mt-1">{kpis.leads}</div>
          <div className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">formulaires contact</div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]"/>
            <Input placeholder="Rechercher par nom, URL, secteur…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="sites-search"/>
          </div>
          <Select value={sectorFilter || "all"} onValueChange={v => setSectorFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="sites-filter-sector"><SelectValue placeholder="Tous secteurs"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous secteurs</SelectItem>
              {availableSectors.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={freshFilter || "all"} onValueChange={v => setFreshFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="sites-filter-fresh"><SelectValue placeholder="Tous statuts"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="fresh">En ligne</SelectItem>
              <SelectItem value="stale">Idle</SelectItem>
              <SelectItem value="offline">Hors ligne</SelectItem>
              <SelectItem value="never">Jamais vu</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Sites grid */}
      {loading ? (
        <Card className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[color:var(--brand-sky)]"/></Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="sites-empty">
          <Globe className="w-10 h-10 mx-auto opacity-30 mb-3"/>
          <p className="font-semibold">{sites.length === 0 ? "Aucun site client connecté" : "Aucun résultat"}</p>
          <p className="text-sm mt-1">{sites.length === 0 ? "Les sites COCKPIT enverront automatiquement un heartbeat toutes les 30 min après appairage." : "Ajustez les filtres."}</p>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="sites-grid">
          {filtered.map(s => {
            const fresh = FRESHNESS_META[s.freshness] || FRESHNESS_META.unknown;
            const SectorIcon = SECTOR_ICONS[s.sector_preset_id] || Building2;
            const mods = s.modules || {};
            return (
              <Card key={s.id} className={`p-5 border ${fresh.border} relative group hover:shadow-md transition-shadow`} data-testid={`site-card-${s.id}`}>
                {/* Status dot */}
                <div className="absolute top-4 right-4 flex items-center gap-1.5">
                  <span className={`w-2 h-2 rounded-full ${fresh.color} ${s.freshness === "fresh" ? "animate-pulse" : ""}`}/>
                  <span className={`text-[10px] font-semibold uppercase ${fresh.text}`}>{fresh.label}</span>
                </div>

                <div className="flex items-start gap-3">
                  {s.branding?.logo_url ? (
                    <img src={s.branding.logo_url} alt={s.company_name} className="w-12 h-12 rounded-lg object-cover bg-white border border-[hsl(var(--border))] shrink-0"/>
                  ) : (
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: s.branding?.primary_color ? `${s.branding.primary_color}22` : "#f1f5f9", color: s.branding?.primary_color || "#64748b" }}>
                      <SectorIcon className="w-6 h-6"/>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display text-lg font-bold text-[color:var(--brand-navy)] truncate pr-16">{s.company_name || "Sans nom"}</h3>
                    {s.site_url && (
                      <a href={s.site_url} target="_blank" rel="noopener" className="text-xs text-[color:var(--brand-sky)] hover:underline flex items-center gap-1 truncate">
                        <ExternalLink className="w-3 h-3 shrink-0"/><span className="truncate">{s.site_url.replace(/^https?:\/\//, "")}</span>
                      </a>
                    )}
                  </div>
                </div>

                {/* Theme & sector */}
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1.5 p-2 rounded bg-[hsl(var(--muted))]/40">
                    <Building2 className="w-3.5 h-3.5 text-[color:var(--brand-sky)]"/>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">Secteur</div>
                      <div className="font-semibold truncate" data-testid={`site-sector-${s.id}`}>{s.sector_preset_id || s.sector || "—"}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 p-2 rounded bg-[hsl(var(--muted))]/40">
                    <Palette className="w-3.5 h-3.5" style={{ color: s.branding?.primary_color || "#64748b" }}/>
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">Thème</div>
                      <div className="font-semibold truncate" data-testid={`site-theme-${s.id}`}>{s.active_theme_name || s.active_theme_id || "—"}</div>
                    </div>
                  </div>
                </div>

                {/* Modules */}
                <div className="mt-2 flex flex-wrap gap-1">
                  {mods.shop && <Badge variant="outline" className="text-[10px] bg-orange-50 text-orange-700 border-orange-200 gap-0.5"><ShoppingBag className="w-2.5 h-2.5"/>Boutique</Badge>}
                  {mods.booking && <Badge variant="outline" className="text-[10px] bg-cyan-50 text-cyan-700 border-cyan-200 gap-0.5"><Calendar className="w-2.5 h-2.5"/>Résa</Badge>}
                  {mods.rental && <Badge variant="outline" className="text-[10px] bg-teal-50 text-teal-700 border-teal-200 gap-0.5"><CalendarRange className="w-2.5 h-2.5"/>Location</Badge>}
                  {mods.food_mode && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 gap-0.5"><Utensils className="w-2.5 h-2.5"/>Food</Badge>}
                  {mods.b2b && <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">B2B</Badge>}
                  {mods.loyalty && <Badge variant="outline" className="text-[10px] bg-pink-50 text-pink-700 border-pink-200 gap-0.5"><Heart className="w-2.5 h-2.5"/>Fidélité</Badge>}
                </div>

                {/* KPIs */}
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded border border-[hsl(var(--border))]">
                    <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">CA 30j</div>
                    <div className="font-display text-sm font-bold text-[color:var(--brand-navy)]" data-testid={`site-revenue-${s.id}`}>{money(s.kpis?.revenue_30d_cents)}</div>
                  </div>
                  <div className="p-2 rounded border border-[hsl(var(--border))]">
                    <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">Commandes</div>
                    <div className="font-display text-sm font-bold text-[color:var(--brand-navy)]">{(s.kpis?.orders_30d || 0) + (s.kpis?.bookings_30d || 0)}</div>
                  </div>
                  <div className="p-2 rounded border border-[hsl(var(--border))]">
                    <div className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">Leads</div>
                    <div className="font-display text-sm font-bold text-[color:var(--brand-navy)]">{s.kpis?.leads_30d || 0}</div>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-3 pt-3 border-t border-[hsl(var(--border))] flex items-center justify-between text-[11px] text-[hsl(var(--muted-foreground))]">
                  <div className="flex items-center gap-2">
                    <Zap className="w-3 h-3"/>v{s.cockpit_version || "—"}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3"/>{fmtRelative(s.minutes_since_heartbeat)}
                  </div>
                </div>

                {/* Hover actions */}
                <button
                  onClick={() => deleteSite(s.id)}
                  className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded hover:bg-red-50 transition-opacity"
                  title="Retirer de l'Accès Agence"
                  data-testid={`delete-site-${s.id}`}
                  style={{ marginTop: "28px" }}
                >
                  <Trash2 className="w-3.5 h-3.5 text-red-500"/>
                </button>
              </Card>
            );
          })}
        </div>
      )}

      {/* Technical note */}
      <Card className="p-4 bg-[hsl(var(--muted))]/30 border-dashed">
        <div className="flex items-start gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <Activity className="w-4 h-4 shrink-0 text-[color:var(--brand-sky)]"/>
          <div>
            <strong>Heartbeat :</strong> chaque site COCKPIT appairé pousse un snapshot complet (secteur, thème, version, modules actifs, KPIs 30j) vers <code>POST /api/agency/heartbeat</code> toutes les 30 minutes. Vous pouvez forcer un envoi manuel depuis chaque site via <code>Admin &gt; Sauvegardes &amp; outils &gt; Push vers l'Accès Agence</code>.
          </div>
        </div>
      </Card>
    </div>
  );
}
